import React from 'react'
import PropTypes from 'prop-types'
import './WingsTabs.css'

/**
 * WingsTabs Component
 * 
 * Tab navigation component based on the Wingspans Style Guide.
 * Displays tabs with default, hover, and selected states.
 * 
 * @component
 * @example
 * // Basic usage
 * <WingsTabs>
 *   <WingsTab selected>Tab 1</WingsTab>
 *   <WingsTab>Tab 2</WingsTab>
 *   <WingsTab>Tab 3</WingsTab>
 * </WingsTabs>
 * 
 * @example
 * // Controlled tabs with onClick
 * const [activeTab, setActiveTab] = useState(0)
 * <WingsTabs>
 *   <WingsTab selected={activeTab === 0} onClick={() => setActiveTab(0)}>First</WingsTab>
 *   <WingsTab selected={activeTab === 1} onClick={() => setActiveTab(1)}>Second</WingsTab>
 * </WingsTabs>
 */
const WingsTabs = ({
  children,
  compact = false,
  borderless = false,
  className = '',
  ...props
}) => {
  const classNames = [
    'w-tabs',
    compact && 'w-tabs-compact',
    borderless && 'w-tabs-borderless',
    className
  ].filter(Boolean).join(' ')

  return (
    <div className={classNames} role="tablist" {...props}>
      {children}
    </div>
  )
}

WingsTabs.propTypes = {
  /** Tab components (WingsTab) */
  children: PropTypes.node.isRequired,
  
  /** Compact variant with smaller spacing */
  compact: PropTypes.bool,
  
  /** Remove bottom border */
  borderless: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

/**
 * WingsTab Component
 * 
 * Individual tab item to be used within WingsTabs.
 * 
 * @component
 */
const WingsTab = ({
  children,
  selected = false,
  disabled = false,
  onClick,
  className = '',
  ...props
}) => {
  const classNames = [
    'w-tab',
    selected && 'w-tab-selected',
    className
  ].filter(Boolean).join(' ')

  return (
    <button
      type="button"
      className={classNames}
      onClick={onClick}
      disabled={disabled}
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      {...props}
    >
      {children}
    </button>
  )
}

WingsTab.propTypes = {
  /** Tab content */
  children: PropTypes.node.isRequired,
  
  /** Whether this tab is selected */
  selected: PropTypes.bool,
  
  /** Whether this tab is disabled */
  disabled: PropTypes.bool,
  
  /** Click handler */
  onClick: PropTypes.func,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

// Export both components
export { WingsTab }
export default WingsTabs
